/**
 * Frontend notification service for handling comment mentions and Slack notifications
 * Uses authenticated axios instance to ensure Stytch JWT is included in all requests
 */
import api from './api';

interface CommentMention {
  type: 'person' | 'node';
  id: string;
  label: string;
}

interface CommentNotification {
  commentId: string;
  nodeId: string;
  nodeLabel?: string;
  authorId: string;
  authorName: string;
  content: string;
  mentions: CommentMention[];
  createdAt: number;
}

class NotificationService {
  constructor() {
    console.log('[NOTIFICATIONS] Initialized with authenticated axios instance');
  }

  /**
   * Send comment mention notification to backend
   */
  async sendCommentMentionNotification(notification: CommentNotification): Promise<boolean> {
    try {
      // Only send if there are person mentions
      const personMentions = notification.mentions.filter(m => m.type === 'person');
      if (personMentions.length === 0) {
        console.log('[NOTIFICATIONS] No person mentions found, skipping notification');
        return true;
      }

      console.log('[NOTIFICATIONS] Sending mention notification for comment:', notification.commentId);
      console.log('[NOTIFICATIONS] Person mentions:', personMentions.map(m => m.label));

      const result = await api.post('/slack/notifications/comment-mention', {
        ...notification,
        mentions: personMentions // Only send person mentions
      });

      if (result.data.success) {
        console.log('[NOTIFICATIONS] ✅ Mention notification sent successfully');
        return true;
      } else {
        console.error('[NOTIFICATIONS] ❌ Failed to send mention notification:', result.data.error);
        return false;
      }
    } catch (error) {
      console.error('[NOTIFICATIONS] 💥 Error sending mention notification:', error);
      return false;
    }
  }

  /**
   * Map a Forethought user to a Slack user
   */
  async mapUser(
    forethoughtUserId: string,
    slackUserId: string,
    slackWorkspaceId: string,
    email?: string
  ): Promise<boolean> {
    try {
      const result = await api.post('/slack/users/map', {
        forethoughtUserId,
        slackUserId,
        slackWorkspaceId,
        email
      });
      
      if (result.data.success) {
        console.log(`[NOTIFICATIONS] ✅ User mapped: ${forethoughtUserId} -> ${slackUserId}`);
        return true;
      } else {
        console.error('[NOTIFICATIONS] ❌ Failed to map user:', result.data.error);
        return false;
      }
    } catch (error) {
      console.error('[NOTIFICATIONS] 💥 Error mapping user:', error);
      return false;
    }
  }

  /**
   * Get user mappings for a Forethought user
   */
  async getUserMappings(forethoughtUserId: string): Promise<any[]> {
    try {
      const result = await api.get('/slack/users/mappings', {
        params: { forethoughtUserId }
      });
      return result.data.mappings || [];
    } catch (error) {
      console.error('[NOTIFICATIONS] 💥 Error getting user mappings:', error);
      return [];
    }
  }

  /**
   * Auto-map users by email for a workspace
   */
  async autoMapUsers(slackWorkspaceId: string): Promise<number> {
    try {
      const result = await api.post('/slack/users/auto-map', {
        slackWorkspaceId
      });
      
      if (result.data.success) {
        console.log(`[NOTIFICATIONS] ✅ Auto-mapped ${result.data.mappedCount} users`);
        return result.data.mappedCount;
      } else {
        console.error('[NOTIFICATIONS] ❌ Failed to auto-map users:', result.data.error);
        return 0;
      }
    } catch (error) {
      console.error('[NOTIFICATIONS] 💥 Error auto-mapping users:', error);
      return 0;
    }
  }

  /**
   * Get notification service health
   */
  async getHealth(): Promise<any> {
    try {
      const result = await api.get('/slack/notifications/health');
      return result.data;
    } catch (error) {
      console.error('[NOTIFICATIONS] 💥 Error getting health:', error);
      return { status: 'error', error: error instanceof Error ? error.message : String(error) };
    }
  }

  /**
   * Check if backend is available
   */
  async isBackendAvailable(): Promise<boolean> {
    try {
      const result = await api.get('/slack/health');
      return result.data === 'ok' || result.data.status === 'ok';
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Export types for use in components
export type { CommentMention, CommentNotification }; 